/**
 * LandingNav — shared top nav for the landing page and the /contact route.
 *
 * iter147 — The `Product` link is now a mega-dropdown grouped into 4 themes
 * (Discover · Analyze · Operate · Extend) driven by `SUITE_GROUPS`. Each
 * item deep-links to `/#suite-{slug}` — the same anchor the Suite section
 * cards render — so the nav never drifts out of sync with the marketing
 * page. On mobile the Sheet expands the same groups as an accordion.
 *
 * Design contract:
 * - Desktop: logo · Product ▾ (mega-menu) · Pricing · Contact · Book a Demo · Sign In · Get Started Free.
 * - Mobile: hamburger Sheet mirroring the desktop layout with a stacked
 *   group list for Product.
 * - Book-a-Demo is parent-owned via `onOpenBookDemo` callback so the parent
 *   controls the modal instance.
 */
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetClose,
} from '@/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import DotboxLogo from '@/components/common/DotboxLogo';
import { Calendar, Menu, ChevronDown } from 'lucide-react';
import { SUITE_GROUPS, MODULE_BY_SLUG } from '@/constants/landing';

const LandingNav = ({ onOpenBookDemo }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const openBookDemo = () => {
    setMobileOpen(false);
    onOpenBookDemo?.();
  };

  // Logo click — clean programmatic control:
  // - Same page (already on `/`) → smooth-scroll to top, no navigation.
  // - Cross page (contact/reschedule/cancel) → navigate to `/` then
  //   instant scroll-to-top so the new render doesn't inherit the
  //   previous route's scroll offset.
  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } else {
      navigate('/');
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
    setMobileOpen(false);
  };

  return (
    <nav
      className="border-b border-[#1F2A4D] bg-[#0A1024]/80 backdrop-blur-md sticky top-0 z-50"
      data-testid="landing-nav"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <a
            href="/"
            onClick={handleLogoClick}
            aria-label="dotbox.ai home"
            data-testid="nav-logo-link"
          >
            <DotboxLogo variant="dark" />
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {/* iter147 — Product mega-dropdown. Radix NavigationMenu opens
                on hover + click for keyboard accessibility. */}
            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger
                    className="bg-transparent text-slate-300 hover:text-white hover:bg-[#131C3A] focus:bg-[#131C3A] data-[state=open]:bg-[#131C3A] data-[state=open]:text-white text-sm font-medium h-9 px-3"
                    data-testid="nav-product-link"
                  >
                    Product
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div
                      className="p-6 w-[720px] lg:w-[880px] bg-[#0F1730] border border-[#1F2A4D] rounded-md shadow-2xl shadow-black/40"
                      data-testid="nav-product-mega-menu"
                    >
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        {SUITE_GROUPS.map((group) => (
                          <div
                            key={group.key}
                            data-testid={`nav-product-group-${group.key}`}
                          >
                            <div className={`text-[10px] uppercase tracking-[0.16em] font-semibold mb-3 ${group.tint}`}>
                              {group.label}
                            </div>
                            <ul className="space-y-1">
                              {group.slugs.map((slug) => {
                                const mod = MODULE_BY_SLUG[slug];
                                if (!mod) return null;
                                const Icon = mod.icon;
                                return (
                                  <li key={slug}>
                                    <Link
                                      to={`/#suite-${slug}`}
                                      className="flex items-start gap-2.5 rounded-md px-2 py-1.5 hover:bg-[#131C3A] transition-colors group"
                                      data-testid={`nav-product-item-${slug}`}
                                    >
                                      <span className="flex-shrink-0 mt-0.5 p-1 rounded-md bg-red-600/10 border border-red-600/30 group-hover:bg-red-600/20 transition-colors">
                                        <Icon className="h-3.5 w-3.5 text-red-400" />
                                      </span>
                                      <span className="min-w-0">
                                        <span className="block text-sm font-medium text-slate-100 group-hover:text-white">
                                          {mod.name}
                                        </span>
                                        <span className="block text-[11px] text-slate-400 leading-snug line-clamp-2">
                                          {mod.blurb}
                                        </span>
                                      </span>
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 pt-4 border-t border-[#1F2A4D] flex items-center justify-between text-xs text-slate-400">
                        <span>Every module ships with tests, keyboard shortcuts, and public API access.</span>
                        <Link
                          to="/#suite"
                          className="text-red-400 hover:text-red-300 font-medium"
                          data-testid="nav-product-see-all"
                        >
                          See the full Suite →
                        </Link>
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link
              to="/#pricing"
              className="px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-[#131C3A] transition-colors text-sm font-medium"
              data-testid="nav-pricing-link"
            >
              Pricing
            </Link>
            <Link
              to="/contact"
              className="px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-[#131C3A] transition-colors text-sm font-medium"
              data-testid="nav-contact-link"
            >
              Contact
            </Link>
            <button
              type="button"
              onClick={openBookDemo}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-slate-300 hover:text-white hover:bg-[#131C3A] transition-colors text-sm font-medium"
              data-testid="nav-book-demo-link"
            >
              <Calendar className="h-3.5 w-3.5" />
              Book a Demo
            </button>
            <Link
              to="/login"
              className="px-3 py-2 rounded-md text-slate-300 hover:text-white transition-colors text-sm font-medium"
              data-testid="nav-signin-link"
            >
              Sign In
            </Link>
            <Button
              asChild
              className="ml-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white shadow-lg shadow-red-900/30"
              data-testid="nav-get-started-button"
            >
              <Link to="/register">Get Started Free</Link>
            </Button>
          </div>

          {/* Mobile: hamburger + Get Started */}
          <div className="md:hidden flex items-center gap-2">
            <Button
              asChild
              size="sm"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white shadow-lg shadow-red-900/30 text-xs"
              data-testid="nav-mobile-get-started-button"
            >
              <Link to="/register">Get Started</Link>
            </Button>
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="p-2 rounded-md text-slate-200 hover:text-white hover:bg-[#131C3A]"
                  aria-label="Open menu"
                  data-testid="nav-mobile-trigger"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-72 bg-[#0F1730] border-l overflow-y-auto"
                data-testid="nav-mobile-sheet"
              >
                <SheetHeader>
                  <SheetTitle className="text-white text-left">Menu</SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-1">
                  {/* iter147 — Product becomes a collapsible accordion of the
                      4 groups. Uses <details>/<summary> to keep the JSX small
                      and dependency-free while remaining keyboard-accessible. */}
                  <details className="group" data-testid="nav-mobile-product">
                    <summary className="flex items-center justify-between px-2 py-2 rounded-md hover:bg-[#131C3A] text-sm text-slate-200 cursor-pointer list-none">
                      <span>Product</span>
                      <ChevronDown className="h-4 w-4 text-slate-400 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="pl-2 pr-1 py-1 space-y-3">
                      {SUITE_GROUPS.map((group) => (
                        <div key={group.key} data-testid={`nav-mobile-product-group-${group.key}`}>
                          <div className={`text-[10px] uppercase tracking-[0.16em] font-semibold mb-1 ${group.tint}`}>
                            {group.label}
                          </div>
                          <ul className="space-y-0.5">
                            {group.slugs.map((slug) => {
                              const mod = MODULE_BY_SLUG[slug];
                              if (!mod) return null;
                              return (
                                <li key={slug}>
                                  <SheetClose asChild>
                                    <Link
                                      to={`/#suite-${slug}`}
                                      className="block px-2 py-1.5 rounded-md text-xs text-slate-300 hover:text-white hover:bg-[#131C3A]"
                                      data-testid={`nav-mobile-product-item-${slug}`}
                                    >
                                      {mod.name}
                                    </Link>
                                  </SheetClose>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      ))}
                      <SheetClose asChild>
                        <Link
                          to="/#suite"
                          className="block mt-2 px-2 py-1.5 rounded-md text-xs text-red-400 hover:text-red-300 font-medium"
                          data-testid="nav-mobile-product-see-all"
                        >
                          See the full Suite →
                        </Link>
                      </SheetClose>
                    </div>
                  </details>
                  <SheetClose asChild>
                    <Link
                      to="/#pricing"
                      className="block px-2 py-2 rounded-md hover:bg-[#131C3A] text-sm text-slate-200"
                      data-testid="nav-mobile-pricing"
                    >
                      Pricing
                    </Link>
                  </SheetClose>
                  <SheetClose asChild>
                    <Link
                      to="/contact"
                      className="block px-2 py-2 rounded-md hover:bg-[#131C3A] text-sm text-slate-200"
                      data-testid="nav-mobile-contact"
                    >
                      Contact
                    </Link>
                  </SheetClose>
                  <button
                    type="button"
                    onClick={openBookDemo}
                    className="w-full text-left inline-flex items-center gap-1.5 px-2 py-2 rounded-md hover:bg-[#131C3A] text-sm text-slate-200"
                    data-testid="nav-mobile-book-demo"
                  >
                    <Calendar className="h-4 w-4 text-red-400" />
                    Book a Demo
                  </button>
                  <SheetClose asChild>
                    <Link
                      to="/login"
                      className="block px-2 py-2 rounded-md hover:bg-[#131C3A] text-sm text-slate-200"
                      data-testid="nav-mobile-signin"
                    >
                      Sign In
                    </Link>
                  </SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default LandingNav;
